import React, { useEffect, useState } from 'react';

interface UnitCountInputProps {
  initialUnitCount: number;
  onUnitCountChange?: (value: number) => void;
  enabled: boolean;
}

const UnitCountInput: React.FC<UnitCountInputProps> = ({ onUnitCountChange, initialUnitCount, enabled }) => {
  const [unitCount, setUnitCount] = useState<number>(initialUnitCount);
  const [unitCountError, setUnitCountError] = useState<string>('');

  useEffect(() => {
    setUnitCount(initialUnitCount);
  }, [initialUnitCount]);

  const validateUnitCount = (value: number) => {
    if (value < 0) {
      setUnitCountError('Unit count must be at least 0');
      return false;
    }
    if (value > 100000) {
      setUnitCountError('Unit count must not exceed 100,000');
      return false;
    }
    setUnitCountError('');
    return true;
  };

  const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
    setUnitCount(value);
    // Clear error when user starts typing
    if (unitCountError) {
      setUnitCountError('');
    }
  };

  const handleUnitCountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const isValid = validateUnitCount(unitCount);
    // Only call the callback if validation passes
    if (isValid) {
      onUnitCountChange?.(unitCount);
      e.target.style.borderColor = 'rgba(16, 29, 59, 0.2)';
    }
  };

  const handleUnitCountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger blur
      // If there's an error after validation, refocus
      setTimeout(() => {
        if (unitCountError) {
          e.currentTarget.focus();
        }
      }, 0);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '3vh',
        padding: '0 1.6vh',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <div
        style={{
          fontSize: '1.4vh',
          fontWeight: '700',
          color: '#101D3B',
          textAlign: 'left',
        }}
      >
        Contracted
        <br />
        Unit Count
      </div>
      <input
        disabled={!enabled}
        type="number"
        placeholder="Enter unit count..."
        value={unitCount === 0 ? '' : unitCount}
        onChange={handleUnitCountChange}
        onBlur={handleUnitCountBlur}
        onKeyDown={handleUnitCountKeyDown}
        min="0"
        max="100000"
        style={{
          width: '100%',
          height: '4vh',
          padding: '1.2vh 1.6vh',
          border: unitCountError ? '0.2vh solid #EF4444' : '0.2vh solid rgba(16, 29, 59, 0.2)',
          borderRadius: '0.8vh',
          fontSize: '1.4vh',
          color: '#101D3B',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'border-color 0.2s',
          backgroundColor: unitCountError ? '#FEF2F2' : 'white',
        }}
        onFocus={e => {
          if (!unitCountError) {
            e.target.style.borderColor = '#38B2AC';
          }
        }}
      />
      {unitCountError && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: '1.6vh',
            marginTop: '0.5vh',
            fontSize: '1.2vh',
            color: '#EF4444',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5vh',
            backgroundColor: 'white',
            padding: '0.8vh 1.2vh',
            borderRadius: '0.6vh',
            boxShadow: '0vh 0.4vh 1.6vh rgba(239, 68, 68, 0.2)',
            border: '0.1vh solid #EF4444',
            zIndex: 1000,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: '1.4vh' }}>⚠</span>
          {unitCountError}
        </div>
      )}
    </div>
  );
};

export default UnitCountInput;
